import { useEffect, useState } from "react";
import { CalendarDays, Plus, Trash2, RefreshCw, Users, Save, ShieldCheck, KeyRound, Loader2 } from "lucide-react";
import {
  loadHariLibur,
  saveHariLibur,
  addHariLibur,
  removeHariLibur,
} from "../utils/hariLiburStorage";
import { HARI_LIBUR_2026 } from "../data/hariLiburData";
import { formatTanggalID } from "../utils/hariKerja";
import { Toaster, toast } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import { api, changePasswordRequest, twoFaSetup, twoFaActivate, formatApiError, hasRole } from "../utils/auth";

const input = "block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20";

function passwordChecks(pwd) {
  return [
    { label: "Minimal 12 karakter", ok: pwd.length >= 12 },
    { label: "Huruf besar (A-Z)", ok: /[A-Z]/.test(pwd) },
    { label: "Huruf kecil (a-z)", ok: /[a-z]/.test(pwd) },
    { label: "Angka (0-9)", ok: /[0-9]/.test(pwd) },
  ];
}

export default function Pengaturan() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("tim");
  const [libur, setLibur] = useState(() => loadHariLibur());
  const [form, setForm] = useState({ tanggal: "", keterangan: "" });
  const [users, setUsers] = useState([]);
  const [editingWA, setEditingWA] = useState(null); // id user
  const [tempWA, setTempWA] = useState("");

  // Keamanan tab state
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { secret, qrPngBase64 } saat re-enroll
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);

  const canEdit = hasRole(user, "IRBAN");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdSubmitting) return;
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Konfirmasi password tidak sama.");
      return;
    }
    setPwdSubmitting(true);
    try {
      await changePasswordRequest(pwdForm.current, pwdForm.next);
      setPwdForm({ current: "", next: "", confirm: "" });
      toast.success("Password berhasil diganti.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setPwdSubmitting(false);
    }
  };

  const startReEnroll = async () => {
    try {
      const data = await twoFaSetup(); // pakai access token (sesi aktif)
      setEnrollData(data);
      setEnrollCode("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const confirmReEnroll = async (e) => {
    e.preventDefault();
    if (enrollSubmitting) return;
    setEnrollSubmitting(true);
    try {
      const userData = await twoFaActivate(null, enrollCode.trim());
      setUser(userData);
      setEnrollData(null);
      setEnrollCode("");
      toast.success("Google Authenticator berhasil didaftarkan ulang.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setEnrollSubmitting(false);
    }
  };

  useEffect(() => {
    if (tab === "tim") {
       fetchUsers();
    }
  }, [tab]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Gagal memuat data pengguna.");
    }
  };

  const saveWA = async (userId) => {
    try {
       await api.put(`/users/${userId}/wa`, { noWA: tempWA });
       toast.success("Nomor WA berhasil diperbarui");
       setEditingWA(null);
       fetchUsers(); // refresh data
    } catch (e) {
       toast.error("Gagal menyimpan nomor WA: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!form.tanggal) return;
    const next = addHariLibur(libur, form.tanggal, form.keterangan.trim() || "Libur");
    setLibur(next);
    saveHariLibur(next);
    setForm({ tanggal: "", keterangan: "" });
    toast.success("Hari libur ditambahkan — perhitungan deadline di seluruh app akan menggunakan data baru.");
  };

  const handleRemove = (tanggal) => {
    const next = removeHariLibur(libur, tanggal);
    setLibur(next);
    saveHariLibur(next);
    toast.success("Hari libur dihapus");
  };

  const handleResetPrefill = () => {
    setLibur([...HARI_LIBUR_2026]);
    saveHariLibur([...HARI_LIBUR_2026]);
    toast.success("Daftar hari libur direset ke prefill standar RI 2026");
  };

  return (
    <div className="space-y-5" data-testid="pengaturan-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-end justify-between border-b border-gray-200 pb-5">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Konfigurasi
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold text-slate-900 md:text-3xl">Pengaturan</h1>
          <p className="mt-1 text-[13px] text-slate-500">Konfigurasi sistem — mempengaruhi seluruh perhitungan di modul Audit.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200" data-testid="pengaturan-tabs">
        {[{ key: "tim", label: "Manajemen Tim", icon: Users }, { key: "libur", label: "Hari Libur", icon: CalendarDays }, { key: "keamanan", label: "Keamanan Akun", icon: ShieldCheck }].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`pengaturan-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-[12.5px] font-semibold ${active ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "libur" ? (
        <section className="space-y-4" data-testid="tab-libur">
          {!canEdit ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800" data-testid="libur-readonly-notice">
              Hanya IRBAN yang dapat menambah atau menghapus hari libur. Anda dapat melihat daftar di bawah (read-only).
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {canEdit ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-1">
              <h3 className="font-display text-base font-semibold text-slate-900">Tambah Hari Libur</h3>
              <form onSubmit={handleAdd} className="mt-3 space-y-3" data-testid="hari-libur-form">
                <label>
                  <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Tanggal</div>
                  <input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} required className={input} data-testid="libur-date" />
                </label>
                <label>
                  <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Keterangan</div>
                  <input type="text" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="mis. Cuti Bersama" className={input} data-testid="libur-keterangan" />
                </label>
                <button type="submit" className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#1e3a5f] py-2 text-[12.5px] font-semibold text-white hover:bg-[#152a46]" data-testid="libur-add-btn">
                  <Plus className="h-3.5 w-3.5" /> Tambah
                </button>
                <button type="button" onClick={handleResetPrefill} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 py-2 text-[11.5px] font-semibold text-slate-700 hover:border-[#1e3a5f]" data-testid="libur-reset-btn">
                  <RefreshCw className="h-3 w-3" /> Reset ke Prefill 2026
                </button>
              </form>
            </div>
          ) : null}

          <div className={`rounded-lg border border-gray-200 bg-white ${canEdit ? "lg:col-span-2" : "lg:col-span-3"}`}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <h3 className="font-display text-sm font-semibold text-slate-900">Daftar Hari Libur ({libur.length})</h3>
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-[12.5px]">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Tanggal</th>
                    <th className="px-3 py-2 text-left font-semibold">Keterangan</th>
                    {canEdit ? <th className="px-3 py-2 text-right font-semibold">Aksi</th> : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white" data-testid="libur-list">
                  {libur.map((h) => (
                    <tr key={h.tanggal} data-testid={`libur-row-${h.tanggal}`}>
                      <td className="px-3 py-2 font-mono">{h.tanggal}</td>
                      <td className="px-3 py-2 text-slate-800">{h.keterangan}</td>
                      {canEdit ? (
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemove(h.tanggal)}
                            data-testid={`libur-remove-${h.tanggal}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3 w-3" /> Hapus
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </section>
      ) : null}

      {tab === "tim" ? (
        <section className="space-y-4" data-testid="tab-tim">
          {!canEdit ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800">
              Hanya IRBAN yang dapat mengubah nomor WA tim pemeriksa. Anda dapat melihat daftar di bawah (read-only).
            </div>
          ) : null}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <h3 className="font-display text-sm font-semibold text-slate-900">Data Tim Pemeriksa & WA</h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-[12.5px]">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Nama</th>
                    <th className="px-3 py-2 text-left font-semibold">Peran</th>
                    <th className="px-3 py-2 text-left font-semibold">Nomor WA</th>
                    {canEdit ? <th className="px-3 py-2 text-right font-semibold">Aksi</th> : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{u.name}</td>
                      <td className="px-3 py-2 text-slate-500">{u.role?.replace("_", " ")}</td>
                      <td className="px-3 py-2 font-mono">
                         {editingWA === u.id ? (
                           <input 
                             type="text" 
                             className={`${input} py-1 px-2 h-7 min-w-[150px]`} 
                             value={tempWA} 
                             onChange={e => setTempWA(e.target.value)}
                             placeholder="Mis. 08123456789"
                             autoFocus
                           />
                         ) : (
                           u.noWA || <span className="text-red-500 italic">Belum diisi</span>
                         )}
                      </td>
                      {canEdit ? (
                        <td className="px-3 py-2 text-right space-x-2">
                          {editingWA === u.id ? (
                             <>
                               <button onClick={() => saveWA(u.id)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-800">
                                 <Save className="h-3 w-3" /> Simpan
                               </button>
                               <button onClick={() => setEditingWA(null)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800">
                                 Batal
                               </button>
                             </>
                          ) : (
                            <button onClick={() => { setEditingWA(u.id); setTempWA(u.noWA || ""); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800">
                              Edit WA
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {tab === "keamanan" ? (
        <section className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="tab-keamanan">
          {/* Ganti Password */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#1e3a5f]" />
              <h3 className="font-display text-base font-semibold text-slate-900">Ganti Password</h3>
            </div>
            <p className="mt-1 text-[12px] text-slate-500">
              Password akun Anda sendiri (NIP {user?.nip || "-"}). Minimal 12 karakter dengan huruf besar, kecil, dan angka.
            </p>
            <form onSubmit={handleChangePassword} className="mt-4 space-y-3" data-testid="keamanan-password-form">
              <label>
                <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Password Saat Ini</div>
                <input type="password" autoComplete="current-password" required value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} className={input} data-testid="keamanan-current-password" />
              </label>
              <label>
                <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Password Baru</div>
                <input type="password" autoComplete="new-password" required value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} className={input} data-testid="keamanan-new-password" />
              </label>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {passwordChecks(pwdForm.next).map((c) => (
                  <li key={c.label} className={`text-[11px] ${c.ok ? "text-emerald-600" : "text-slate-400"}`}>
                    {c.ok ? "✓" : "•"} {c.label}
                  </li>
                ))}
              </ul>
              <label>
                <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Ulangi Password Baru</div>
                <input type="password" autoComplete="new-password" required value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className={input} data-testid="keamanan-confirm-password" />
              </label>
              <button
                type="submit"
                disabled={pwdSubmitting || !passwordChecks(pwdForm.next).every((c) => c.ok) || !pwdForm.confirm}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#1e3a5f] py-2 text-[12.5px] font-semibold text-white hover:bg-[#152a46] disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="keamanan-password-submit"
              >
                {pwdSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Simpan Password Baru
              </button>
            </form>
          </div>

          {/* 2FA */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#1e3a5f]" />
              <h3 className="font-display text-base font-semibold text-slate-900">Verifikasi Dua Langkah (2FA)</h3>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${user?.totpEnabled ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`} data-testid="keamanan-2fa-status">
                {user?.totpEnabled ? "Aktif ✓" : "Belum aktif"}
              </span>
              <span className="text-[12px] text-slate-500">Google Authenticator wajib untuk semua akun.</span>
            </div>
            {!enrollData ? (
              <div className="mt-4">
                <p className="text-[12px] text-slate-500">
                  Ganti HP atau aplikasi Authenticator hilang? Daftarkan ulang. Kode lama akan hangus dan Anda langsung memindai QR baru.
                </p>
                <button
                  type="button"
                  onClick={startReEnroll}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-[12px] font-semibold text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                  data-testid="keamanan-2fa-reenroll-btn"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Daftarkan Ulang Authenticator
                </button>
              </div>
            ) : (
              <form onSubmit={confirmReEnroll} className="mt-4 space-y-3" data-testid="keamanan-2fa-enroll-form">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-center">
                  <img
                    src={`data:image/png;base64,${enrollData.qrPngBase64}`}
                    alt="QR Google Authenticator"
                    className="mx-auto h-40 w-40 rounded bg-white p-2 ring-1 ring-gray-200"
                  />
                  <div className="mt-2 text-[11px] text-slate-500">Tidak bisa scan? Masukkan manual:</div>
                  <code className="mt-1 inline-block select-all rounded bg-white px-2 py-1 text-[11px] font-semibold tracking-wider text-slate-800 ring-1 ring-gray-200">{enrollData.secret}</code>
                </div>
                <label>
                  <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Kode 6 Digit</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className={`${input} text-center text-[16px] font-semibold tracking-[0.4em]`}
                    data-testid="keamanan-2fa-code"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={enrollSubmitting || enrollCode.length !== 6}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#1e3a5f] py-2 text-[12.5px] font-semibold text-white hover:bg-[#152a46] disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="keamanan-2fa-confirm-btn"
                  >
                    {enrollSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Konfirmasi
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEnrollData(null); setEnrollCode(""); }}
                    className="rounded-md border border-gray-200 px-3 py-2 text-[12px] font-semibold text-slate-600 hover:text-slate-900"
                  >
                    Batal
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
